/**
 * How the dashboard works — written down once, used twice.
 *
 * This is the only description of Agent Care's screens that exists. Two things
 * read it:
 *
 *   1. Pearl. `web/functions/api/chat.ts` appends it to her system prompt, which
 *      is what lets her answer "how do I…" instead of saying she wasn't told.
 *   2. Renee, directly, on the Guide tab (`web/src/routes/Guide.tsx`).
 *
 * They import the same constant on purpose. A guide that drifts from what Pearl
 * says is worse than no guide, because both sound authoritative and only one can
 * be right. **If you change a screen, change this file in the same commit.**
 *
 * Written for Renee, not for a developer: no file names, no table names, no
 * jargon. Every claim was read out of the code and then checked back against it
 * a second time, adversarially — that pass found a dozen errors in the first
 * draft, including a Send button described as not existing while sitting on the
 * Pearl tab, and a DEPARTED badge placed on the wrong half of the screen. Assume
 * the same of any future edit: what the screen obviously does and what it does
 * are different questions.
 *
 * Where the app can show something confusing — a blank field, an expired
 * session, a count that disagrees with the tab above it — this says so plainly.
 * Those are exactly the moments she will ask.
 */

export const DASHBOARD_GUIDE = `# Agent Care — how the dashboard works

Agent Care is at https://reneeroark.com. You sign in with Google, as
renee.roark@therealbrokerage.com.

## Signing in

The sign-in screen is a single card with a **Sign in with Google** button. It
also says "Access is by invitation. There is no sign-up" — accounts have to be
added in advance, so there is nothing to fill in.

**Use the browser you are signed into renee.roark@therealbrokerage.com with.**
That is the same account the reviewed emails go out from, so one window does
both jobs and Gmail never opens the wrong mailbox. renee@reneeroark.com still
works as a sign-in if you ever need it.

If you sign in with a Google account that hasn't been added, you get a **No
access** card instead, with a **Try a different account** button and a note to
ask Chris to add you.

## The parts of the screen

Once you're in, every screen has the same two things across the top, and the
agent lists have a third.

**The title bar.** "Agent Care" on the left, a search box in the middle, and on
the right your name — your email address if no name is on file — and a **Sign
out** button. An OWNER badge appears beside the name on accounts marked as owner;
other accounts show no badge.

**The tabs**, underneath. Four are lists of agents — All, Capped, Elite (12 mo),
Team Leaders — and each shows a running count beside its name. Then
Scheduled Sends, Templates, What's Missing, Requests, Pearl, and Guide (this
page).

**The filter row**, underneath the tabs, on the four agent tabs only. It shows a
count, three dropdowns (area, MLS, tag), a **More filters** button, a **Clear
filters** button, a **Mailing list** button once you have ticked somebody, an
**Export** button, a **Tag** button, an **Email** button, a **Views** button, an
"Include departed" tick box, and a standing note on the right: "Test data · the
existing dashboard is still the record · nothing sends without you". That note is
there because your old dashboard is still the official system until we switch
over.

**Clear filters** sits right there in the row and turns itself grey when there is
nothing to clear. It is the one to reach for — the three dropdowns can narrow
your list without the More filters panel ever being opened, so a Clear hidden
inside that panel would be the last place you would look.

**The count in the filter row is always the All figure**, whichever agent tab you
are on — so on the Capped tab it shows how many agents match in total, not how
many capped ones. It moves as you search and filter, just as the tabs do. The tab
is the number that answers "how many capped agents match what I'm looking at".

## Finding an agent

**Type in the search box at the top.** It matches on first name, last name, team
name, team role, primary market, region, email address, Real email, MLS, licence
number, phone number, home, mailing and office addresses, service area, and any
tag you have added. Partial words work — typing "reed" finds Dana Reed.

**You can type a whole name.** "Chris Bice" works, and so does "Bice, Chris" —
first and last name are stored separately, so the two halves are looked for
separately when the whole thing is not found in one place. The same goes for a
name plus a team, a region or a tag: "Bice Columbia" finds Chris Bice in
Columbia.

That second pass only looks at **who somebody is** — their name, team, role,
region, market, service area, email, MLS and tags. It deliberately does not
spread across addresses or your notes, because a stray word matching somebody's
street would put a person in your Email count with nothing on screen to explain
why. Addresses and notes are still fully searchable; you just have to type the
words together, the way you would remember them.

Each word has to be at least three letters for this to happen. "a b" is not a
search, and left to itself it would have matched everybody.

**Phone numbers can be typed however you like.** 8035541202, 803-554-1202 and
(803) 554-1202 all find the same person; only the digits are compared. Type at
least three digits for that to happen.

**It also searches the notes you have written.** If you noted "moving to
Charlotte in the spring" on someone six months ago, typing part of that finds
them. Searching a note never changes it — notes are still only ever written by
you.

**The search applies to every tab at once, and the tab counts change as you
type.** If you search "Smith" and the Capped tab then says 3, that means three
capped agents match "Smith" — not that you have only three capped agents. Clear
the search box to get the real numbers back.

### Narrowing further

The three dropdowns in the filter row stack on top of the search and on top of
each other:

- **Area** — the agent's region, or their primary market if no region is set.
  Agents with neither appear under "Unassigned".
- **MLS** — their primary MLS. "Unassigned" if there isn't one.
- **Tag** — one of your own tags.

Each starts at "All areas" / "All MLS" / "All tags", which means no filtering.
Set them back to those to clear.

When nothing matches, the list says **"No agents match."**

### More filters

**The "More filters" button** opens a row of further dropdowns. They work
exactly like the three above — every one you set narrows the list further:

- **Licence expires** — a specific expiry date, or "No date on file".
- **Licence status** — *already expired*, *within 30 / 60 / 90 days*, *later
  than 90 days*, or *no date on file*. The windows nest, so "within 90 days"
  includes the ones due next week rather than hiding them in a different
  option. **This is the one to look at first**: a number of agents on the
  roster already have an expiry date in the past. The What's Missing tab says
  how many, today.
- **Address looks odd** — records holding more than one thing that looks like a
  zip code, usually a new address pasted in front of an old one. The What's
  Missing tab counts them.
- **Licence expires in** — a whole year, for a renewal cohort. Worth knowing
  that the two are not the same question: 386 licences expire during 2027, but
  only 370 of them on 30 June. Sixteen fall on other days, and picking the date
  rather than the year would leave those sixteen out.
- **Region**, **Primary market**, **Team**, **Team role**. Agents on no team
  appear under "Not on a team" in Team; agents on a team with no role recorded
  appear under "No role recorded" in Team role — two different facts, kept
  apart.
- **Birthday month** and **Anniversary month**.
- **Capped in** and **Reached Elite in** — the month and year an agent most
  recently crossed that line, for finding everyone who capped or reached
  Elite in a given month. These show full history, every month anyone has on
  file.
- **Joined in** — the month and year an agent joined Real. This one only
  offers a **recent rolling window** rather than full history — the last six
  months, though the window starts at July 2026 so it shows fewer than six
  until January 2027 — because this is a "who's new" search, not a permanent
  record of everyone's join date. It does not include anyone picked up
  through the South Carolina sweep just below; they were already with Real,
  so nothing "joined" on the date they were added.
- **Newly SC-licensed in** — the same rolling window, for agents who turned
  up already licensed and active in South Carolina without ever appearing in
  New Joiners.
- **Mailing address**, **Home address** and **Photo** — each offering "On file"
  or "Missing", for finding the people something is missing for.

**The dropdowns only offer values somebody actually has** — going by the whole
roster, or by the roster including departed agents if that box is ticked. If no
agent's licence expires in March 2029, March 2029 is not in the list. They do
**not** take account of the tab you are on, the search box, or the other
dropdowns, so once you have set one filter another one can still come back with
nobody. The lists also change as the roster does.

If a value you have picked stops existing — you filtered to a team only departed
agents are on, then unticked "Include departed" — the dropdown keeps showing it
with **"— no matches"** after it, so an empty list always has a visible reason.

**The button shows how many filters are on** — "More filters · 2" — so a filter
you set and forgot cannot quietly shrink your list. **Clear all filters** at the
end of the row resets every one of them at once, including the three in the
filter row above. It does not clear the search box or "Include departed".

### Getting the list out — Export

**The Export button** in the filter row takes whatever is on screen and turns it
into a file. Whatever you have narrowed to — the tab, the search box, every
dropdown — is exactly what comes out.

Click it and it tells you what it is about to do before you choose:

    370 agents selected
    347 of 370 have an address · 23 without

**Spreadsheet (Excel)** gives you every column: names, both email addresses,
phone, the address split into street, town, state and zip, which address was
used, the address exactly as it is stored, team, team role, area, MLS, licence
number and expiry, birthday, work anniversary, and whether they are capped,
Elite, a team leader, active or departed. **If anyone in your selection has no
address, they get a second tab of their own** — "No address" — with their name,
email, phone, team and area, so the people you cannot post to are a list you can
work through rather than a silence.

**One label per household.** Where two or more agents in your selection share an
address — married couples, family teams — a tick box appears offering to put
them on one label together, as "Ben Jaeger and Sarah Jaeger". It tells you how
many agents that affects and how many envelopes it saves.

It only appears when there is somebody to group, it is **off by default**, and
it goes back off by itself when you change tab: a licence renewal is addressed
to the person it belongs to and only they should open it. For a Christmas card,
tick it.

Addresses are matched **exactly as they are stored**, so a couple who typed
theirs slightly differently — one "St", one "Street" — still get a label each.

**Mailing labels (Word)** produces a Word document laid out for **Avery 5160**
labels — 30 to a sheet, three across and ten down. Open it and print it; there
is nothing to set up. It tells you how many sheets you will need. **Only agents
with an address appear on labels**, which is why the count above matters.

A label is usually three lines: name, street, then town, state and zip.

**About the addresses — worth reading once.** They are stored as one long line,
so they have to be taken apart to make a label. The zip and the state come off
the end reliably. Splitting what is left into street and town is a **best
guess**, because most records have nothing marking where one ends and the other
begins. It gets there for 729 of the 730 agents who have an address; the one it
cannot split keeps everything on one line, which still posts perfectly well.

Because it is a guess, **the spreadsheet keeps the address exactly as stored in
its own column**, right next to the split-up version. A glance down the Town
column before printing is worth the ten seconds. Where a record looks like it
holds two different addresses jammed into one field, the **Check address**
column says "Check" — those are worth looking at properly.

**Where an agent has no mailing address, their home address is used** — that is
65 more people who can be posted to. The spreadsheet's "Address used" column
says which one, for every row.

### Saving a list you use often — Views

**The Views button** remembers a selection so you don't have to set it up again.
Get the list how you want it — filters, search, and which tab you are on — then
open **Views**, type a name at the bottom, and press **Save**. From then on it
is one click, and it takes you back to the tab you saved it from.

Each saved list shows what it will set and how many agents it currently matches,
so you can see at a glance whether "Licence renewals 2027" still means what it
did. The **×** beside one forgets it.

**Saved lists live in this browser.** They are yours, on this computer — they
will not appear if you sign in from a different machine, and they are not
something Chris can see. Saving one over an existing name replaces it.

## The mailing list

Every agent in the list has a **tick box** on the left. Ticking people builds a
mailing list — a group you are collecting in order to email them.

**The Mailing list button appears in the filter row as soon as you tick
somebody**, with the count on it. When the list is empty the button is not
there — except straight after a list has gone away, however that happened
(cleared, cancelled, or spent on a send), when it stays put with no number on it
so that **Put them back** is still in reach. Nothing is hidden by this: the tick
boxes are always there, and an empty basket is not a thing you need a button
for. Most of the time — searching one person and pressing Email — you will never
see it.

**Above the list there is a bar with two things on it.**

- **Select all 58** ticks everyone currently on screen in one go. Once they are
  all ticked it changes to **Unselect these 58**. If some are already ticked, it
  adds the rest.
- **Clear** empties the whole mailing list — which may be more people than you
  can see, since the list survives changing the filters. It says how many are on
  it right next to the button, and **Put back** appears immediately afterwards
  if you did it by mistake.

The bar stays put while you scroll, so it is there when you want it.

**The list is not a tag.** A tag is permanent and searchable; a mailing list is
for one job and disappears when you are done with it. If you want to keep a
group for next month, tick them and then use **Tag** — that turns the list into
something that lasts.

### Building a list across more than one filter

This is the part worth knowing, because it is the only way to ask an "or"
question.

Every dropdown in the filter row *narrows* — area **and** MLS **and** team. There
is no way to type "Greenville area or the Greenville MLS" into a filter. So you
do it in two passes:

1. Filter one way. Open **Mailing list** and click **Add 24**.
2. Change the filters completely. Open it again and click **Add 9**.

**Ticking survives a filter change**, which is what makes this work, and
**nobody is ever added twice**. If eleven of the second group were already on
the list, the button says **Add 9**, not Add 20, and it tells you afterwards
that eleven were already there.

### Seeing who is actually on it

Because the list survives filter changes, you will often have people on it who
are not on screen. That is normal, not a mistake.

Tick **Show only these** in the Mailing list menu and the roster shows exactly
your list, so you can read it before you send anything. Untick anyone you did
not mean to catch.

**While that is on, the dropdowns and the search box are ignored.** That is
deliberate — you built the list by filtering two different ways, so the last
filter you used is still set, and applying it would show you a fraction of your
own list. The toolbar says **Mailing list · 273** while this is on, with a
**Show everyone** button next to it.

While that is on, **Export and Tag act on the mailing list too**, since they
both work on whatever is on screen. So you can tick a group, show only them, and
export a spreadsheet of exactly those people.

### Clearing it

**Clear all**, in the Mailing list menu, asks you to confirm and then empties the
list. **Take off N**, in the same menu, removes just the people currently on
screen — which on the All tab with no filters is everybody, so it is the same
thing by another name.

**Clear**, in the bar above the list, empties the whole list on one click and
does *not* ask first. Put back is how you undo it, not a confirmation.

**Cancel in the email composer empties it too.** Cancel means "forget this one",
and leaving the list ticked behind you is how five people get an email you meant
to send to one. Nothing is queued by cancelling — no email has been written down
at any step before "Yes, queue it".

**So does a send that goes through.** The list has done its job at that point,
and if it stayed ticked the next email would quietly go to those people again as
well as the new ones. **A send that fails part-way leaves the list alone**, so
you can simply press the button again — the ones that already saved are
recognised and left alone, and only the missing ones are added. The screen tells
you both numbers.

Whichever way it happened, **Put back N** appears above the list, says how many,
and restores both the list and the Show-only-these setting. It is there even
when the roster is showing nobody. Two things to know about it: it lives above
the roster, so after a send that dropped you on the Outbox you have to come back
to an agent tab to find it — and ticking anyone before you press it loses the
undo.

## What's Missing

**A tab that shows where the roster has holes**, and how many agents are in each
one. Ten of them: licences already expired, licences expiring within 90 days, no
address at all, no mailing address, no home address, an address worth checking,
no licence expiry date, no photo, no birthday, and no work anniversary.

Each row has a **Show me** button that puts exactly those agents on the agent
list, where you can look at them, work through them, or Export the list to chase
them.

**The counts follow the roster**, so they go down as the gaps get filled. A row
showing zero is greyed out with **None** instead of a button. Departed agents
are not counted.

Nothing on this tab is a fault. It is what 785 agents' worth of records
collected from two different systems actually looks like — the point is that you
can see it rather than discover it one agent at a time.

### Sorting the list

**Sort by** sits at the end of the More filters row:

- **Last name (A–Z)** — the normal order.
- **Licence expiry (soonest first)**
- **Birthday (coming up first)**
- **Work anniversary (coming up first)**

For the three that are not last name, each agent in the list gains a second line
underneath showing that date, so you can see what the order is based on. Agents
with no date go to the bottom of the list rather than the top — a blank is not
"soonest".

### Agents who have left

**Departed agents are hidden by default, but they are never deleted.** Tick
"Include departed" in the filter row to see them. In the list they appear faded.
Open one and their name carries a **DEPARTED** badge; if a departure date is on
file, it also says when they left and that their history is kept. Everything you
ever wrote about them is still there.

If a search finds nobody, it is worth ticking that box before concluding the
person isn't in the system.

## The agent tabs

**All** is every active agent.

**Capped** means capped in the current cap cycle rather than everyone who has
ever capped — the cycle resets each year. One exception worth knowing: if an
agent has no cap reset date on file, any cap date at all counts, so a few older
ones can appear here.

**Elite (12 mo)** means an agent who became Elite within the last twelve months.

**Team Leaders** is everyone marked as a team leader.

The number beside each tab is how many agents are in it right now, with your
search and filters applied.

## Looking at one agent

The agent tabs are split in two: the list of agents on the left, the details of
whoever you have selected on the right. Before you pick anyone, the right side
says **"Select an agent"**.

**Click a name in the list to see their details.** The list stays where it is —
you don't lose your place, and you can click straight down through several
agents.

**Your selection clears whenever the agent you picked drops out of the list in
front of you** — because you typed in the search box, changed a dropdown, ticked
or unticked "Include departed", or switched to another tab. Going to Pearl and
coming back clears it too. Nothing is lost; you just click the name again.

In the list, each agent shows their photo in a circle, their name, any badges,
their team or area underneath, and their tags. Where there is no photo you see
their initials in the circle instead. The badges are:

- **ELITE** (purple) — Elite in the last twelve months
- **CAP** (green) — capped in the current cycle
- **LEAD** (gold) — team leader

### What the detail panel shows

At the top: the agent's photo — larger, with softly rounded corners rather than
a circle — their name with the same badges beside it, and underneath their team,
team role and area. Agents without a photo show their initials there instead.

Below that, three cards — Contact, Professional, Milestones — side by side if the
window is wide enough, stacked if it isn't. Then full-width sections underneath
them.

**Contact** — Email, Real email, Phone, Home, Mailing, Office. Email, Real email
and Phone each have a small **copy** button, which puts the value on your
clipboard and flashes "Copied". The copy button only appears when there is
something to copy.

**Professional** — Area, MLS, State, Licence, Licence expires, Service area,
Pending, Closed, Active listings, Network.

**Milestones** — Birthday, Work anniversary, Capped, Elite, First closing, Cap
resets. Where a work anniversary has a year on record, the anniversary line also
shows how many years they have been with Real.

**Bio** — only appears if there is one.

Then **Notes**, **Tags**, **Outreach history**, and — only if that agent has any
— **Drafts**.

### "Not known yet"

Any field with nothing in it says **Not known yet** rather than sitting blank.
That wording is deliberate: it means nobody has filled it in, not that the
answer is nothing.

**Birthdays especially.** Birthdays arrive from two places — reZEN, where agents
fill them in themselves and often don't, and Metabase, which is always right but
only reveals a birthday on the day that agent's alert fires. So the number of
known birthdays grows steadily across the year. A blank birthday means "we
haven't seen it yet", never "she doesn't have one".

**Birthdays are a month and a day, with no year.** That is on purpose. The
dashboard will never show an agent's age because it does not know it.

## Notes

Each agent has one notes box on their detail panel. It is greyed out for a
moment while it loads. Type in it and click **Save** — the button reads
"Saving…" briefly and a small "Note saved" message appears at the bottom of the
screen. Underneath the box it tells you either "Last saved" with the date and
time, or "Never saved".

**Nothing automated ever writes in here.** Not an import, not a sync, not Pearl.
These are your words and only a person puts them there.

Pearl can *read* a note when you ask her about that agent — she just cannot
change one.

## Tags

Tags are your own labels — whatever is useful to you.

**To add one:** type it in the "Add a tag" box at the bottom of the Tags section
and press Enter, or click **Add**.

**To remove one:** click the small × on the tag.

If there are none yet it says "No tags yet." Tags show up in three places: on the
agent's detail panel, under their name in the list, and in the tag dropdown in
the filter row, so you can pull up everyone carrying a tag.

### Tagging everyone on screen at once

The **Tag** button in the filter row, next to Export, works on whatever the
filters have narrowed the list to — the same list Export would use.

Open it and it tells you how many agents are selected. Type a tag, or start
typing and pick one you have used before. Underneath, it splits the selection
two ways: how many already have that tag, and how many do not.

Then there are two buttons, and each one says exactly what it will do:

- **Tag 374 agents** — adds the tag to everyone on screen who does not already
  have it. Nobody gets it twice.
- **Remove from 12 agents** — takes it off everyone on screen who has it.
  Nothing else about them changes.

**Above 25 agents it asks you to confirm first**, and shows the number again. It
also asks whenever the tag is one the outreach system acts on, however few
agents are involved — see below.

**Undo.** After either button runs, it says what it did and offers **Undo that**.
The undo acts on exactly the agents that were changed, even if you have altered
the filters since — so a tag applied to the wrong 386 people takes one click to
put back, not 386.

**Tags that change who gets email.** A few tag names are not just labels. Any tag
beginning \`no-\` and ending \`-email\` — and \`no-outreach\` — stops the outreach
system writing drafts for that agent. The menu warns you when you type one and
always asks you to confirm, because applying one to a whole list quietly turns
off email for all of them.

**The two that stop your emails are shown by what they do.** On the agent card
and in the list they appear in amber, under plain names rather than the names
they are stored under, with a line saying what each one still lets through:

- **No one-to-one emails** — stored as \`no-outreach\`. Left out of birthdays,
  anniversaries and anything you read and send one at a time. **Still receives
  mass emails.**
- **No mass emails** — stored as \`no-bulk-email\`. Left out of anything sent to
  everybody at once. **Still receives one-to-one emails** like a birthday note.

Neither one means "never contact this person" — that is **both** of them, and an
agent with both says "No emails at all" instead of the two half-truths.

**Search and the tag dropdown still use the stored name.** Type \`no-outreach\`
to find them, not "No one-to-one emails". The stored name is printed in small
type next to the explanation on the card so the two are never a guess.

**Capital letters do not create a second tag.** "VIP" and "vip" are the same tag
here; it is stored lower-case either way, so the dropdown never ends up with two
entries that should have been one.

**If something goes wrong halfway**, it tells you how many were changed and how
many were not, rather than just saying it failed. The ones that went through
stay, and Undo still covers them.

## Outreach history

On each agent's detail panel, this lists what has been sent to them and when —
the occasion (Birthday, Work anniversary, Cap reached, Elite reached, First
closing, Welcome), which occurrence it was, a status, and a date. If there is
nothing, it says **"Nothing recorded."**

**"baseline" means the event happened before this system existed.** It was
recorded so that nothing gets drafted for it a second time. Nobody sent
anything. That explanation only appears when there is history to explain.

## Emailing a group

The **Email** button in the filter row writes to whoever is on the list — your
mailing list if you have ticked people, otherwise everyone the filters are
showing. It asks three things, in this order.

**1. Who, and how.** The number is at the top, along with anyone who cannot be
emailed and why — no address on file, left the brokerage, or tagged to be left
alone. Those are out before you write anything. Underneath is the real
question, and there are three ways to answer it:

> Do you want to read and send each one yourself, send everybody the same
> message, or set this up to repeat on a schedule?

It tells you roughly how long reading them all would take, so at 12 people the
answer is obvious and at 386 it is obvious the other way.

**"Repeat this on a schedule" only appears when you got here from a filter or
tab** — Capped, Elite, a saved View, and so on — never from a mailing list you
ticked by hand. That's deliberate: a recurring campaign has to work out who
matches, fresh, every single time it fires, and a ticked list is frozen the
moment you tick it — it could never notice someone new. If you want a
recurring campaign, don't tick anyone first.

**2. Write it.** Start from a template or a blank page. Give it a short name for
your own benefit — "2027 licence renewals" — so you can find it later, in the
Outbox or on the Scheduled Sends tab.

**If you chose to read each one**, you can drop names and other details into the
message with the buttons under it, and you get to see how it reads for a real
agent before you go on. **If you chose the same message to everybody, or to
repeat it on a schedule, you cannot** — and it will refuse rather than let you.
Nobody reads those before they leave, so there is no one to notice a name that
came through wrong.

**If you chose to repeat it on a schedule**, there's an extra step here: pick
how often (weekly, every few weeks, monthly on a date, or monthly on a
weekday), which day it needs, and what time of day (America/New York). A
sentence underneath spells out exactly what you've set, in plain words, before
you go on.

**3. Confirm.** The number is said out loud one more time, along with anyone
being left out, and then — for the first two choices — it goes in the Outbox.
**A recurring campaign goes to the Scheduled Sends tab instead**, as a
standing rule rather than something waiting to be sent today. Nothing is sent
the day you create it; each time it's actually due, a fresh one is generated
and that's what lands in the Outbox for you to review and send.

**Reading it yourself, to exactly one person, skips the Outbox entirely
(2026-08-23) — when the shortcut actually goes through.** The button on that
last screen says "Yes, open it in Gmail" instead of "Yes, queue it," and
pressing it opens a Gmail draft directly — addressed, filled in, ready to
send — with nothing parked in the Outbox for it. Come back and press "It
went" once you've actually sent it there. This is the one-recipient case
only; anyone else — two people or more, same message to everybody, or a
recurring campaign — still goes in the Outbox exactly as described below.

A few things quietly fall back to the ordinary Outbox path even when it
really is one person: a message too long for Gmail's own link to carry, a
pop-up your browser blocked, a save that failed, or the identical subject
already sent to that same person earlier today. None of those count as
something going wrong — the card just lands in the Outbox the normal way,
same as it would for two people or more, and you send it from there.

**Nothing sends at this point.** None of the choices ends with an email
already gone.

**If you chose to read each one and it all went through, you land straight on
the Outbox** — there is nothing to tell you, so it does not stop to say it. You
only see a "Done" page when there is something worth reading: some of them were
already waiting from earlier today, or part of it did not save. (The
one-recipient shortcut above is the one exception when it goes through clean —
it never lands on the Outbox at all, waiting or not. When it doesn't go
through clean, see the fallback cases described just above it.) **The same
message to everybody always stops**, because what it says there is an
instruction rather than a receipt — it is being *held*, and it goes nowhere
until you open the Bulk tab.

**Sending the same subject twice in one day is treated as the same job.** If
something goes wrong half way through, press **Try the rest again** — the ones
that already saved are recognised and left where they are, you get the missing
ones and nothing else, and the screen says how many of each.

Three things follow from that, and they are the whole of it:

- **The identical subject cannot go to the same person twice in one day.** The
  second attempt reports them as already queued. Change a word in the subject
  line and it goes through as its own email.
- **Changing the message but keeping the subject is refused outright**, and it
  says so. Nothing is written and nothing is lost — but the earlier wording is
  what is sitting in the Outbox, so either change the subject or delete that
  batch first. It will never quietly keep the old words and tell you everything
  is fine.
- **"Already queued" counts ones you have already sent.** If it says all 40 were
  queued earlier today and the Outbox is empty, that is because you worked
  through them this morning — not because they are hiding.

**There is a Cancel on every one of those screens**, next to Back. No email is
written down until you press **Yes, queue it** on the last one. If you are not
sure whether something already happened: if you have not pressed that button,
it has not.

Cancel does two things — it leaves the composer, and it **empties your mailing
list**, so the next email starts from nothing rather than from people you ticked
and thought better of. A send that goes through empties it as well, for the same
reason; a send that fails part-way leaves it alone.

If you did not mean to lose the list, **Put back N** undoes it. It lives above
the roster on the agent tabs — so after cancelling you land right on it, and
after a send that dropped you on the Outbox you have to click back to All to
reach it. Ticking anyone before you press it loses the undo.

## Templates

The wording you start from, kept here rather than in the code so you can change
it whenever you like. Saving takes effect straight away.

**To make a new one**, type a name at the top and press Create — "Licence
renewal", "Welcome". A name is all it needs; the subject and the message are
written underneath once it exists.

**Give each one a name you can tell apart.** The name is the only thing shown for
a template, both here and in the list you pick from when writing an email. Two
called "Welcome" look identical in both places, and there is no way to see which
is which. If you type a name that already exists it will say so — it still lets
you, because sometimes that is what you meant.

A new template is empty, and an empty one is **not offered** when you write an
email. It appears in the list once it has a subject or a message.

**Below the sendable ones, under a pink banner, are the notes marked GUIDANCE.**
Those are notes about how to write — voice, how to handle a belated message, how
to handle an early one. They are notes to yourself, not emails, so they have **no
Subject box** and their text box is labelled **Notes**. Nothing will ever send
them, and they cannot be deleted.

**To delete one**, press Delete on it and confirm. It asks first, and there is no
undo. **Deleting a template does not touch anything you have already sent** —
picking a template *copies* its words into the emails at that moment, so every
email that has ever gone out keeps its own wording forever.

**The guidance notes at the bottom cannot be deleted.** They are notes about how
to write rather than emails, nothing will ever send them, and there would be no
way to get one back.

### Dropping in names and other details

Under each template are buttons for dropping in a detail about the agent:
First name, Last name, Team, Primary market, Region, Licence number, Licence
expiry, Cap resets, Years with Real, Phone, Birthday, Join date, Date became
capped, Date became Elite, Time from joining to cap, Time from cap to Elite,
Pending transactions, Closed transactions, Active listings, Revenue-share
network size, and Bio. **If there is no button for it, it cannot be filled
in.** They put a fill-in **where your cursor is**, so click into the middle of
a sentence first. If you have not clicked into the box at all, it goes on the
end. They work on the subject too: click into the subject box and the buttons
switch to it.

**Dates come out spelled properly** — "February 1, 2027", not the way the
database stores it. Cap reset dates are always the 1st of a month, so word it as
the date rather than as a day in the middle of the month.

**Years with Real is only known for some agents**, and grows on its own as
more of it is confirmed — nothing needs re-sending as coverage improves.

**A detail that isn't known for a particular agent leaves the fill-in tag
showing** — \`{{years}}\`, for instance — rather than a blank space, so a gap
is something you notice and can fill in by hand, never a silently missing
sentence.

**Use the buttons rather than typing the fill-ins.** Spelling and capitals both
matter — a capitalised First_Name is not the same as first_name — and anything we
do not recognise goes out looking exactly as you typed it. If you do mistype one,
a red line under the message says so, and the email will refuse to send until it
is fixed.

**A template with a fill-in in it can only be used for "read and send each
one".** A line under the message says so as soon as you add one. That is
deliberate: nobody reads a "same message to everybody" before it leaves, so
there would be no one to notice a name that came out wrong. If you want a
template for those, leave the fill-ins out.

### Adding a link

**The Insert link button** works the same way as the fill-in buttons, for a
web address. Click into the message where you want it — or select some words
first — and press it; it asks for the address and drops in a plain link,
typed out in full (\`https://...\`), right there in the text. Leave off
\`https://\` when you type it and it is added for you. If you selected words
first, they stay next to the link rather than being replaced by it, as
"click here (https://...)".

**It only ever inserts the address itself — never words that hide it, the way
"click here" can work in Word.** That is deliberate, not a shortcut left
half-built: a reviewed email leaves as a Gmail draft with the message as
plain text, and a bulk send goes out as real HTML — two different things
happening to the same words, and a link with hidden text would quietly break
in whichever of the two doesn't support it. A plain web address is the one
form that reads correctly in both.

**In the reviewed lane (Gmail), the address shows as plain text while you're
still writing the draft** — that's normal, not a sign it didn't work. **In a
bulk send, it becomes a real clickable link automatically**, since that lane
builds the email as real HTML rather than opening a Gmail draft.

## Triggers

**The Triggers tab** lists the eight milestones that draft their own emails
without anyone writing one by hand, and lets you turn each one on or off.
Every one of them already ran before this tab existed — what's new is being
able to see, and switch off, each one from the dashboard itself.

Each shows, as a pill at the top of its card, the name of the template on the
**Templates** tab it drafts from — worded slightly differently there in a
couple of cases, but always the same milestone. A drafted email lands in the
**Outbox** for review exactly the way a manual one does; nothing goes out on
its own.

**The switch on each one** stops it going forward. Anything it already
drafted stays put in the Outbox, and an event that happens while it's off is
recorded but not drafted — so switching it back on later does not suddenly
draft a backlog of everything that happened while it was off.

The eight:

- **Birthday** — fires on the day itself. Suppress tag: \`no-birthday-email\`.
- **Work anniversary** — fires on the day itself. Suppress tag:
  \`no-anniversary-email\`.
- **Reached cap** — fires when an agent reaches cap. Suppress tag:
  \`no-capped-email\`.
- **Reached Elite** — fires when an agent reaches Elite. Suppress tag:
  \`no-elite-email\`.
- **First closing** — fires on an agent's first closed transaction. Suppress
  tag: \`no-firstTransaction-email\`.
- **Joined** — fires when a new agent joins, and starts the welcome sequence
  rather than sending one email alone. Suppress tag: \`no-joined-email\`.
- **Departed** — fires when an agent leaves, a farewell sent for review like
  any other. **No suppress tag** — this one has no per-agent opt-out; it is
  either on for everyone or off for everyone.
- **Newly SC-licensed** — fires when an agent appears in the full South
  Carolina roster without ever having appeared in New Joiners — already with
  Real, newly active or licensed in this state. Not a welcome; they aren't
  new to the company. Suppress tag: \`no-sc-licensed-email\`.

**A suppress tag opts one agent out** without touching the switch — tag that
one person and this milestone stops drafting for them, while it keeps
running for everyone else.

### Candidates — not built yet

Underneath the eight, a second list names things that could become a trigger
because the data for them already exists, but don't fire anything today:
another closing after the first, a first listing taken, and a reZEN-sourced
change such as a new mailing address. None of them draft anything. Building
one for real is a request to Claude, the same as any of the eight above once
were.

## Drips

A drip campaign is a sequence of emails to the same people — the first one now,
the next after however many days you choose, and so on. Any number of touches,
any gap between them.

**A "Read and send each one" touch that comes due writes itself into your
Outbox automatically, overnight** — the same daily update that brings in new
agents and milestones. You do not have to open this tab for it to happen; the
Outbox is the one place you check every day, and drip touches land there on
their own. **Approve** is still here for when you want a touch in the Outbox
sooner than tomorrow morning, or as a manual fallback if a campaign's touches
have stopped writing themselves in.

**Landing in the Outbox is not the same as sending.** Automatic or by hand,
approving a touch only writes the emails into your **Outbox**, where you still
read and send them one at a time exactly as you would any other queued email.

### The two kinds

**Read and send each one** puts a card per agent in the Outbox, goes out through
your Real Brokerage Gmail, and can fill in names. This is what a new-agent
sequence uses.

**Same message to everybody** goes out in one batch through Resend and cannot
use names — the same rule as anywhere else, because nobody reads a bulk email
before it leaves. **Group sending is not switched on yet.** It needs the Resend
upgrade. You can build these campaigns and put people in them now; the approve
button is not there until that is done.

### Making one

Give it a name, choose which of the two it is, and press Create. Then **Open**
it and add touches. The first touch is day zero — the day somebody joins the
campaign. Every touch after it is "this many days after the one before", so 0,
30, 30 means day 0, day 30 and day 60.

Each touch has its own subject and message, and can start from a template. The
words are **copied** when you pick the template, so changing that template later
never rewrites a touch — and never rewrites an email already sent. Boxes save
when you click away from them.

**A touch has to be at least one day after the one before it**, and it will say
so rather than accepting nought.

**A touch that has already gone out to somebody cannot be deleted.** The record
of what those people were sent hangs off it. Change its wording instead, or end
the campaign and start another.

**Mistyped fill-ins are refused here too.** If a touch says "frist_name" in
double braces, approving it is turned down and tells you why, the same as in the
composer.

### Putting people in

Tick people on any agent tab the way you would to email them, come back here,
and press **Add the N on your list**.

**Check which campaign you are pressing it on.** Every campaign has that same
button in the same place, and the only difference is the name above it. The
confirmation appears on the campaign you added to, rather than at the top of the
page, so you can see where they landed.

**Who is in a campaign is a snapshot, not a filter.** The people you added are
in it; changing the filters afterwards does not add or remove anybody. So a
licence-renewal campaign is the 214 people who matched when you started it, and
it stays those 214.

**Everybody has their own day zero** — the day they were added. Somebody added
today gets touch 2 thirty days from today, not thirty days from when the
campaign was made. That is what makes a new-agent sequence work: agents joining
in different weeks each get the same emails the same number of days apart.

Adding somebody who is already in a campaign does nothing — they keep their
place rather than starting again.

### While it runs

Each campaign shows **active**, **paused** or **finished**, and each person
shows where they are: *touch 2 of 4, next 12 September*.

- **Pause** stops touches coming due. Nobody is removed and nothing is lost.
- **End it** marks the whole campaign finished.
- **Stop**, next to a person, takes that one person out. It asks first, and
  **Put back in** undoes it. They **pick up from today**: their remaining
  touches run at the usual spacing from the day you put them back, rather than
  all arriving at once because they were out for months. Nothing they have
  already had is sent again. Put somebody back the same week and their dates do
  not move at all.
- **Reopen**, on a finished campaign, makes it active again. Ending a campaign
  is not permanent.
- **Delete** appears only once a campaign is finished. It asks first and says how
  many touches and people it is about to remove.

**Nothing else ever removes people.** Approving does not, sending does not, and
ending a campaign does not. A campaign keeps the people you put in it until you
take somebody out by hand.

**An active campaign that cannot do anything says so on its card** — no touches
yet, a touch with nothing written in it, nobody in it, or everybody stopped. An
ACTIVE campaign with two touches and no people looks ready and is not, so it
tells you rather than waiting to be noticed. Paused and finished campaigns do
not carry these notices, because a campaign that is off is not supposed to be
doing anything.

**People can only be added to a running campaign.** Adding them to a paused or
finished one is refused and says why — otherwise forty people would sit in last
year's sequence, never receiving anything, until somebody deleted it.

**Deleting a campaign does not delete emails.** Anything of that campaign's
already sitting in your Outbox stays there — those are real emails you were
about to send, and they are yours to send or delete yourself. **The record of
what was already sent stays too**, on each agent's card, so tidying up a finished
campaign never puts a hole in somebody's history. What goes is the campaign, its
touches and its list of people.

**One touch at a time, per person.** If you have not opened the app for a
fortnight, somebody who never got touch 1 is offered touch 1 — not touches 1, 2
and 3 in the same morning. Once touch 1 has gone, touch 2 becomes due.

**A touch that came due while you were away is still waiting.** It says how
overdue it is rather than pretending it is due today. Nothing expires. For a
"Read and send each one" campaign, the overnight update is writing each one
into the Outbox as it comes due whether or not anybody opens this tab — so
"waiting" there means waiting in the Outbox to be sent, not waiting here to be
approved. Group-sending campaigns still wait for a person, since automatic
approval only ever applies to the read-and-send kind.

**Nobody can get the same touch twice.** Each one is stamped with the campaign
and the touch number, so approving the same batch again writes nothing rather
than sending a second copy.

**People who cannot be emailed are dropped from a batch automatically** — no
address, left the brokerage, or tagged out of that kind of email — the same
rules the composer uses. The due line says how many that is *before* you press
anything, and the button says the number it will actually write.

If **nobody** in a batch can be emailed, the button offers to skip that touch
for them instead. Skipping emails nobody; it records that the touch was
considered and deliberately not sent, so the campaign can move on rather than
offering the same impossible batch every morning.

**The overnight update never skips on its own.** If a batch has nobody who can
be emailed, or a touch has a mistyped fill-in it cannot send as written, the
daily update just leaves it due and quietly tries again the next morning —
which fixes itself the moment the address is added, the tag comes off, or the
wording is corrected. Skip is a decision only you make, here, by hand — an
impossible batch stays offered every morning until either it is fixed or you
skip it.

## The Outbox tab

**Check this tab every day. It's the one place everything automated lands —
milestones, drips, and scheduled/recurring campaigns — so there's nowhere else
you need to remember to look.** (Before 2026-08-22, a scheduled campaign
surfaced on the Outreach tab instead — if that's what you remember, it moved
here so everything is one screen, not two.)

Where emails wait for you, grouped by the job they came from — so "2027 licence
renewals, 32 left" stays one thing rather than 32 loose cards.

**A bulk send shows up differently from a milestone or drip.** Whether it
came from a recurring campaign firing or you built it yourself with "Same
message to everybody," it appears in its own "Bulk sends" section at the
top, as a single card — a subject, how many people it matched, and two
buttons: **Review & send** and **Skip**. There's nothing to review one at a
time; press Review & send and it opens on the Bulk tab, exactly as if you'd
built it yourself moments ago, where you send a test and then confirm the
count.

**Skip gets a bulk send off this list for good**, one of two ways depending
on what has actually happened to it: if nothing has gone out yet, it's
deleted outright, same as Delete on the Bulk tab; if a send was already
started, it's stopped for good instead — the same as pressing **Stop this
send** on the Bulk tab — and it moves over there, where it can be hidden
once you're done looking at it. Either way, it asks once before doing
anything, and it stops showing up here the moment it's done.

**Two panes.** The jobs are listed down the left, each with how many are left and
when it was queued. Click one and all of its emails appear on the right, already
open — there is nothing to expand. Finish the last email in a job and the job
disappears from the list and the next one down takes its place, so you carry
straight on.

The date matters more than it sounds: two jobs can easily have the same name, and
"queued 6:34 PM" is often the only thing that tells them apart.

Each card shows who it is going to and exactly what it says. Then:

- **Send** — opens the message in your Gmail, already filled in. **It does not
  send it.** Read it there, change anything, press send in Gmail.
- **It went** — appears only after you have opened Gmail. Press it once you have
  actually sent, and the card clears.
- **Copy** — puts the message on your clipboard, for pasting somewhere that is
  not Gmail. The subject is not included.
- **Skip** — you do not want this one. It goes away.

The two steps are on purpose: this dashboard cannot see inside your Gmail
window, so it asks rather than assuming. A record of what you sent is only worth
having if it is true.

**You can edit the message on the card.** Click into it and type. Whatever the
box says is what Gmail opens with, and it is saved back onto the card the moment
you press Send — so what you actually sent and what the system remembers stay
the same thing. A note appears under the box while there is an unsaved change.

Editing in the Gmail window works too, and always has. The difference is that a
change made there is gone once you close it, and a change made here is kept.

**The subject is not editable**, on purpose. The subject is how a job is
grouped and counted on this screen, so letting it differ card by card would
quietly split one job into several.

### Throwing a whole job away

**Delete this batch** is at the top of the right-hand pane, above the emails. It
asks first, and then the job is gone — every email still waiting in it, no undo.

**Anything you already sent from that job is kept.** Delete only removes the ones
still waiting; a card you pressed "It went" on has become part of the record and
stays there. So deleting a job you were half way through is safe: it clears what
is left and does not rewrite what you did.

**That is a different thing from Skip**, and the difference is worth knowing.
Skip says *I read this one and decided not to send it*, which is a decision about
a person, so the record keeps it. Delete says *this job should never have existed*
— wrong group, wrong wording, queued by mistake. Nobody was written to, so there
is nothing to keep, and a row saying you declined to email 32 people would be
recording a decision you never made.

Use Skip for one person you have thought about. Use Delete for a job you did not
mean to create.

Your signature still has to be added in Gmail before you send — that is a Gmail
limitation and nothing here can fix it.

## The Bulk tab

**This is the only screen in Agent Care that actually sends email itself.**
Everywhere else, the app writes a draft and you press send in Gmail. Here the
sending service sends it, to everybody on the list, in one go — and there is no
undo. Read this section before you use it.

Bulk emails are not written here. You build one on the roster: filter or tick the
people you want, press **Email**, and choose *"Same message to everybody"*. That
saves it. This tab is where a saved one is opened, checked and sent.

**Nothing on this tab sends on its own, and nothing on a schedule can start one.**
A bulk email goes only when you open it, send yourself a test, and type the
number of emails about to go.

### Sending one

Each saved email is a card. The card shows its name, when you saved it, how many
agents will receive it, and one of three badges: **held** — saved, never sent —
**sent**, or **stopped** — started, then deliberately called off before it
finished (see "Stopping one partway", below).

**Open it.** You see the subject and the whole message exactly as it will arrive.

**Send a test to me.** This sends the email to you and to nobody else. Read it in
your own inbox: check the From line, check your signature, and check how it looks
on a phone, because most of them will be read on one.

**Type the number.** Underneath, the screen tells you how many emails are about
to go and asks you to type that number into the box. The Send button stays dead
until the number matches. Every other confirmation in Agent Care is a button,
because everything else can be undone. This one asks you to type, because typing
is what makes a person actually look at the number.

**The number is how many emails are about to go — not how big the list was when
you saved it.** Those are different on a send you are picking up again, and it is
the first number that matters.

**Send it.** The button says how many people it is about to reach, and beside it:
*"This cannot be undone, and nobody reads the copies."*

### While it is going

It goes out in batches, so it takes a couple of minutes. **Keep the page open
until it finishes** — there is a red line at the top of the tab saying so while
a send is running, and everything else on the tab is paused until it is done.

The card counts as it goes: how many have been sent, how many are still to go,
how many are going out right now, how many were taken off the list after it
started, and how many were refused.

**"Unknown" means handed over with no answer back.** Those are not being sent
again automatically. If you see a number there, check Resend before deciding
whether anybody needs it resending — sending again is how somebody gets it twice.

If the page is closed or the connection drops halfway, nothing is lost. Open the
card again and it tells you how far it got and offers to send the rest. It will
not start from the beginning.

### Who actually receives it

Before you send, the card lists anybody who has fallen off the list since you
saved it, by name where there are only a few:

- people who have **left the brokerage**
- people tagged **"no bulk emails"**
- people with **no usable email address**
- people no longer in the roster at all

They are already taken out of the number. You do not have to do anything about
them — the list is telling you why it is smaller than you remember.

**If the tags fail to load, sending is switched off.** The screen says so and
offers to try again. Without the tags the app cannot tell who has asked not to
receive bulk email, and sending anyway would email the people who asked you not
to.

**If the number is zero**, everybody on that list has left, been tagged, or has
no address. Build the list again from the roster.

### Where it comes from

A bulk email leaves from **renee@reneeroark.com**, and replies come back to
**renee.roark@therealbrokerage.com** — the inbox you actually read. That is
different from the one-at-a-time emails, which leave from your Real Brokerage
address. The lane decides it; there is nothing to choose.

### Stopping one partway

**Stop this send** appears once a send has actually started, in place of
Delete (see below — a send that has begun can't be deleted). Press it and
confirm, and whatever hasn't gone out yet will not go out on its own, ever —
even in the background, even overnight. What already went is untouched and
stays sent.

This is not the same as closing the tab or walking away. A send that is
merely interrupted picks itself back up on its own within a few minutes, by
design, so it survives a closed laptop. **Stop this send** is the deliberate
version — for when the remaining people genuinely should not get this
email.

The card then reads **stopped** instead of "started," with **Carry on**
still there below if it turns out it should actually finish after all — Stop
is not permanent in that sense. Once stopped, **Hide from this list** also
becomes available, same as a finished send, so a stopped campaign can be
tucked away rather than sitting at the top of the tab indefinitely.

### Deleting one

**Delete this** removes the saved email and the saved list of people with it. It
asks once to be sure. It sits deliberately away from the test button, and only
appears before a send has started — once one has, even a stopped one, deleting
it would destroy the record of who already received it, so Hide (above) is the
way to get it out of view instead.

Deleting a bulk email you have already sent does not unsend anything.

## The Outreach tab

**No longer in the tab bar, as of 2026-08-23.** It was a read-only,
newest-first feed of every milestone draft — birthdays, anniversaries and
the like, in every status: pending, sent, skipped — kept only so it could be
cross-checked against what your old dashboard was still actually sending
while both systems ran side by side. It was never a to-do list; there was no
way to send or mark a draft sent from it, on purpose.

Nothing about milestone drafts changed underneath — they're still
generated, still tracked as pending, sent, or skipped, exactly as before.
Two other places already cover what that feed was for:

- **What's waiting on you** — a pending milestone draft still shows on the
  **Outbox**, same as everything else automated.
- **What's already been sent to one agent** — see **Outreach history**,
  above, on that agent's own detail panel.

## The Scheduled Sends tab

**This is where a recurring campaign lives as a standing rule — not where you
send anything.** The day-to-day work (reviewing and sending what it actually
produces) happens on the Outbox, same as everything else automated. Come here
to check on the rule itself, or to pause or end it.

Build one from the roster: filter to the group you want, press **Email**,
then choose **"Repeat this on a schedule"** directly — not "Same message to
everybody" first, which commits to a one-off send instead and never offers a
repeat option afterward.

Each campaign's card shows:

- **Schedule** — the cadence in plain words (weekly on a day, every few
  weeks, monthly on a date or a weekday) and the time it fires, America/New
  York.
- **Next send** — the next date it's actually due. If today's has already
  gone out, it says so and shows the date after that instead of confusingly
  repeating today.
- **Sends to** — which group it targets: a tab (All, Capped, Elite, Team
  Leaders), plus any area, MLS, or tag filter layered on top. Whoever matches
  is worked out fresh every time it fires — never a frozen list from the day
  you built it.

A campaign that's switched off carries a **PAUSED** badge, and **Pause** /
**Resume** are right there on the card. **Delete** ends it for good — anything
it already sent stays sent and on record, this only stops it firing again —
but it's blocked while something it produced is still sitting unsent
somewhere; send or pause first.

**"What it has produced"** lists every actual send this campaign has
generated so far, each with its date, its status, and how many people it
reached (or was planned to). **These are separate from "Sends to" above** —
one is the standing rule, the other is what it's actually done.

## Tasks

**A task is always about one agent — there's no standalone to-do list.**
To create one, open that agent's own record — the same place as Notes and
Tags — and there's a Tasks section: type what it is, optionally give it a
due date, and add it. Check it off when it's done, or delete it if it no
longer applies.

**The Tasks tab (in the main navigation) is a rollup across every agent** —
every open task, sorted by due date, agent, or done/not-done, with a count
at the top and a way to show or hide the ones already checked off. Press
the agent's name on any row and it lands you on the roster with them found
for you. **You can't add a task from this tab** — only from the agent's own
record — this tab is for seeing everything at once, not for creating.

**"Reminder" means the tab surfaces what's due — nothing emails or notifies
you.** Checking the tab (or watching for the number on it) is the whole of
it. An overdue task's date is shown in red so it stands out from one that
still has time.

## Bounces

A bounce or spam complaint against one of an agent's addresses — automatic
for the Bulk lane (Resend tells the app directly), reported by hand for
the Reviewed lane (Gmail), since the app has no way to see inside Gmail.

**To report one by hand**, open the agent's own record. If it's tied to a
specific email you already sent, there's a one-click "Mark as bounced" right
on that Sent row. For anything else — a whole domain down, or one from
before this was tracked — there's a general report box further down: address,
bounced or complained, and an optional reason.

**The Bounces tab is the same rollup pattern as Tasks** — every bounce,
across every agent, newest first, with who reported it (Resend automatically,
or "reported by hand") and when. **Delete removes a bounce from this list once
it's been addressed** — there's no separate "resolved" flag; deleting it is
how you mark it handled.

## The Requests tab

Things you have asked for that the dashboard cannot do yet. Ask Pearl — see
below — and what you asked for lands here, and Chris is emailed as well.

Each one shows the title, who asked and when, the request itself, a status, and
a **Reply** box. It also shows what you were trying to achieve, and which part of
the dashboard it concerns, where those were captured. The statuses are **new**,
**planned**, **building**, **done** and **declined**. The count at the top is how
many are still open.

Chris writes back in the Reply box, so this is where to look for an answer.
Nothing gets built because it was asked for — this is a list of requests, not a
plan, and "declined" is a real answer.

**The status and the Reply box are ordinary boxes, and there is a Save button
under each request.** Nothing you change takes effect until you click it, and
moving to another tab loses anything unsaved. You can edit them too, not just
Chris — so it is worth not typing over his reply by accident.

The wording of a request itself cannot be edited by anyone, on purpose. What you
asked for stays as you asked it.

Requests are never deleted. Something you took the trouble to ask for gets
declined, not disappeared.

**Once a request is set to done or declined it folds out of the list**, so the
page only shows what is still live. It has not gone anywhere — the notice at the
top says how many are closed, and **Show 3 closed** brings them back so you can
read the reply on something finished.

If one says **not emailed**, it is still safely saved here — only the
notification to Chris didn't go out. Tell him if you see it.

## Asking for something new

If you find yourself thinking "I wish it could…", tell Pearl. She will:

1. Tell you honestly whether the dashboard can already do it.
2. Ask what you are trying to achieve, which is usually the more useful half.
3. Read it back to you.
4. **Only once you say yes**, send it to Chris and save it on the Requests tab.

She is told not to file anything you haven't confirmed, so you can think out
loud. If one ever appears on the Requests tab that you didn't agree to, say so —
that is worth knowing about.

**One at a time.** If you ask for three things in a single message she will only
send the first; put the others in separate messages.

**She will not tell you how hard it is or when it might be done**, and that is on
purpose — she has no idea, and a confident guess would mislead you both. That
answer comes from Chris, on the Requests tab.

## The Pearl tab

Pearl is the assistant. Ask her about your roster in plain language — "what's
Dana Reed's phone number", "how many agents capped this cycle", "who's on the
Smith team" — and she looks it up and answers. She can also help you word
something, and explain how any part of this dashboard works.

**Typing:** Enter sends your message; Shift+Enter starts a new line. There is
also a **Send** button.

While she works you'll see "Thinking…", and if she's looking something up, a
line in italics telling you what she's looking for. Then the answer arrives a
few words at a time.

She reads. She cannot change your data: no editing agents, no writing notes or
tags, no marking outreach sent, and she never emails an agent. The one thing she
can write is a feature request — see above — and only after you have confirmed
it.

**The conversation is not saved.** Leaving the tab and coming back starts fresh.
If one conversation gets very long she will say so and ask you to start a new
one, which also makes her answers sharper.

### Asking Pearl to build the list for you

**You can describe the group instead of setting the dropdowns.** Ask Pearl for
"everyone whose licence expires in 2027", or "the agents with no address", and
she works out which filters that means and offers them: a small card appears
under her reply with a **Show me** button and a line saying exactly what it will
set.

**Nothing changes until you press it.** The card is an offer. Press **Show me**
and you land on the agent list with those filters already set — and from there
it is an ordinary list, so Export, sorting and the dropdowns all work as usual.

If she has understood you slightly differently from how you meant it, you will
see it immediately, because you are looking at the people rather than a number.
Adjust the dropdowns from there, or tell her and ask again.

## The Guide tab

This page. The same text Pearl has been given, so asking her and reading this
should never give you two different answers.

## When something looks wrong

**"Your sign-in expired."** Your session ran out. Sometimes the dashboard puts you
straight back on the sign-in card, with a pink note at the top saying why — click
**Sign in with Google** again.
Sometimes it asks you to refresh the page first. Either way nothing has happened
to your data, and signing in again is the whole fix.

**"Your browser blocked the Gmail window."** Pressing Send tries to open a new
window and your browser stopped it. Allow pop-ups for this site, then press Send
again. Nothing was recorded — the card is still waiting, exactly as it was.

**"Reload the page to see what is actually left."** Something did not finish
cleanly, and what is on screen may no longer match what is really there. That
message stays put on purpose while everything else comes and goes. Reload before
you carry on, and do not send from the list until you have.

**A blank white page after an update.** Your browser is holding onto an old copy
of the site. You should see a card headed "Agent Care needs a refresh" with a
**Repair and reload** button — click it.

If repair doesn't take, the button changes to **Try again** and the card tells you
the site may still be finishing its update: wait a minute, then try again or press
Ctrl+Shift+R (Cmd+Shift+R on a Mac). Do that first — it is the simplest thing that
usually works.

If the page is still blank after that, press F12, then right-click the reload
arrow at the top left and choose **"Empty Cache and Hard Reload"**. That is the
one that has always worked when nothing else did. None of this signs you out.

**A field says "Not known yet".** Nobody has filled it in. That is the system
being honest, not a fault.

**An agent shows initials instead of a photo.** There is no photo stored for
them — most of the roster has one, and the What's Missing tab says how many do
not. You also see initials
for a second or two when the page first opens, while the dashboard asks for the
picture links; after that, a photo that has not arrived yet is an empty grey
square rather than initials. The links expire after several hours and the
dashboard renews them by itself, so leaving the page open all day is fine.
Nothing is broken either way.

**"Could not load the roster."** Refresh and sign in again. If it persists, tell
Chris what the message said in full.

**A short technical-sounding message you don't recognise.** Saving a note, adding
a tag or loading a page can occasionally show the database's own words rather than
plain English — usually after a sign-in has expired, sometimes when a tag already
exists. Try again; if it comes back, send Chris a screenshot. It is worth more
than a description.

**No Send button on a draft.** On the Outbox, every card should have one — if
one doesn't, tell Chris. On an agent's own card, **Outreach history** is
read-only by design — a record of what already went, not a place to act on
anything. (Pearl has a Send button, but that just sends her your message.)

**A tab count looks too low.** Check whether there is anything in the search box
or whether one of the dropdowns is still set to a filter.

**The filter row disagrees with the tab.** It always shows the All count. Trust
the number on the tab.
`
